import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ANIMATION_VARIANTS, HERO_CONTENT } from '../../constants';
import { 
  ImageOptimizer, 
  usePerformanceMonitoring 
} from '../../utils/performance';
import { GlassCard, FeatureList } from '../animations';
import { useOptimizedBackground } from '../../hooks/useAnimations';
import { handlePageNavigation } from '../../utils/navigation';

interface ServiceTab {
  id: string;
  label: string;
  title: string;
  description: string;
  image: string;
  features: string[];
}

const ServicesSection: React.FC = () => {
  usePerformanceMonitoring('ServicesSection');
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Memoized services data
  const services: ServiceTab[] = useMemo(() => [
    {
      id: 'built-to-suit-gpu-clusters',
      label: 'Built-to-Suit GPU Clusters',
      title: 'Built-to-Suit GPU Compute Clusters',
      description: 'Custom-designed high-performance GPU computing clusters optimized for demanding computational workloads. Our solutions are tailored to your specific requirements, ensuring maximum performance and efficiency for your applications.',
      image: '/assets/images/services/Data_Center_Img.jpg',
      features: [
        'Custom hardware configuration based on workload requirements',
        'Latest NVIDIA GPU technology for optimal performance',
        'Professional installation and setup services',
        'Scalable infrastructure design',
        '24/7 monitoring and maintenance support'
      ]
    },
    {
      id: 'nvidia-gpu-node-leasing',
      label: 'NVIDIA GPU Node Leasing',
      title: 'NVIDIA GPU Node Leasing',
      description: 'Flexible GPU computing resources available on-demand. Access powerful NVIDIA GPU nodes without the capital investment, perfect for projects with varying computational needs and scalable workloads.',
      image: '/assets/images/services/nvidia-gpu.jpg',
      features: [
        'Pay-as-you-use pricing model for cost efficiency',
        'Instant provisioning and scaling capabilities',
        'High-performance NVIDIA GPU access',
        'Professional support and optimization services',
        'Flexible contract terms'
      ]
    },
    {
      id: 'on-demand-cloud-gpu-compute',
      label: 'On-Demand Cloud GPU Compute',
      title: 'On-Demand Cloud GPU Compute',
      description: 'Scalable cloud-based GPU computing resources that adapt to your needs in real-time. Whether you need additional compute power for specific projects or ongoing operations, our cloud solution provides the flexibility and performance you require.',
      image: '/assets/images/services/On_Demand_Compute.jpg',
      features: [
        'Auto-scaling based on workload demands',
        'Cloud-based deployment for maximum accessibility',
        'Integrated monitoring and analytics dashboard',
        'Flexible contract terms and pricing options',
        'High availability and reliability'
      ]
    },
    {
      id: 'ai-software-solutions',
      label: 'AI Software Solutions',
      title: 'AI Software Solutions',
      description: 'Comprehensive AI software development and implementation services. From custom AI models to enterprise-grade applications, we deliver software solutions that drive business value and innovation.',
      image: '/assets/images/services/ai-solutions.png',
      features: [
        'Custom AI model development and training',
        'Enterprise AI application integration',
        'Machine learning pipeline development',
        'Real-time analytics and insights',
        'Ongoing maintenance and optimization support'
      ]
    }
  ], []);

  const [activeTab, setActiveTab] = useState(services[0].id);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Get current service index
  const currentServiceIndex = useMemo(() => 
    services.findIndex(service => service.id === activeTab),
    [services, activeTab]
  );
  
  // Optimized tab change handler with transition state
  const handleTabChange = useCallback((serviceId: string) => {
    if (serviceId === activeTab || isTransitioning) return;
    
    setIsTransitioning(true);
    setActiveTab(serviceId);
    
    // Reset transition flag after animation - optimized timing
    setTimeout(() => setIsTransitioning(false), 500);
  }, [activeTab, isTransitioning]);
  
  // Navigation functions
  const navigateToPrevious = useCallback(() => {
    const prevIndex = currentServiceIndex > 0 
      ? currentServiceIndex - 1 
      : services.length - 1;
    handleTabChange(services[prevIndex].id);
  }, [currentServiceIndex, services, handleTabChange]);
  
  const navigateToNext = useCallback(() => {
    const nextIndex = currentServiceIndex < services.length - 1 
      ? currentServiceIndex + 1 
      : 0;
    handleTabChange(services[nextIndex].id);
  }, [currentServiceIndex, services, handleTabChange]);
  
  // CSS containment style - REMOVED to allow Globe layering
  // const containmentStyle = useMemo(() => 
  //   CSSContainment.createContainmentStyle(['layout', 'style', 'paint']), []
  // );

  // Optimized background style
  const { backgroundClasses, backgroundStyle } = useOptimizedBackground(
      HERO_CONTENT.backgroundImage,
      'linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.8))'
    );

  // Memoized active service
  const activeService = useMemo(() => 
    services.find(service => service.id === activeTab) || services[0],
    [services, activeTab]
  );

  // Get the service URL path based on the service ID
  const getServicePath = useCallback((serviceId: string) => {
    const servicePathMap: Record<string, string> = {
      'built-to-suit-gpu-clusters': '/services/gpu-clusters',
      'nvidia-gpu-node-leasing': '/services/gpu-leasing',
      'on-demand-cloud-gpu-compute': '/services/on-demand',
      'ai-software-solutions': '/services/ai-solutions'
    };
    return servicePathMap[serviceId] || '/services';
  }, []);

  // Preload all service images
  useEffect(() => {
    const serviceImages = services.map(service => service.image);
    ImageOptimizer.preloadImages(serviceImages).catch(() => {});
  }, [services]);

  // Memoized tab navigation elements with enhanced animations
  const tabElements = useMemo(() => 
    services.map((service, index) => (
      <motion.button
        key={service.id}
        onClick={() => handleTabChange(service.id)}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ 
          delay: index * 0.1, 
          duration: 0.4,
          ease: [0.25, 0.25, 0, 1]
        }}
        whileHover={{ 
          scale: 1.05,
          transition: { duration: 0.2, ease: "easeOut" }
        }}
        whileTap={{ 
          scale: 0.98,
          transition: { duration: 0.1 }
        }}
        className={`px-8 py-3 rounded-xl font-medium transition-all duration-300 service-tab ${
          activeTab === service.id
            ? 'btn-futuristic shadow-lg shadow-primary-500/25'
            : 'btn-outline hover:border-primary-400/50 hover:bg-primary-500/10'
        }`}
        style={{
          willChange: 'transform, opacity',
          transform: 'translateZ(0)',
          minWidth: 180,
          backfaceVisibility: 'hidden'
        }}
      >
        {service.label}
      </motion.button>
    )), [services, activeTab, handleTabChange]
  );

  return (
    <section 
      className="relative section-padding" 
      id="Services"
    >
      {/* Optimized Background */}
      <div 
        className={backgroundClasses}
        style={{...backgroundStyle, transform: 'translateZ(0)'}}
      />
      
      <div className="container relative z-40">
        {/* Section Header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={ANIMATION_VARIANTS.slideUp}
          className="text-center mb-16"
        >
          <motion.h2 
            className="heading-1 mb-6"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.25, 0.25, 0, 1] }}
            viewport={{ once: true }}
          >
            Our <span className="text-primary-gradient">Services</span>
          </motion.h2>
          <motion.p 
            className="body-large max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2, ease: [0.25, 0.25, 0, 1] }}
            viewport={{ once: true }}
          >
            Discover how our cutting-edge GPU computing and AI software services can transform your 
            high-performance computing needs and drive exceptional results for your projects.
          </motion.p>
        </motion.div>

        {/* Tab Navigation - Desktop */}
        {!isMobile && (
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={ANIMATION_VARIANTS.slideUp}
            className="flex flex-wrap justify-center gap-4 mb-12"
          >
            {tabElements}
          </motion.div>
        )}
        
        {/* Mobile Service Navigation */}
        {isMobile && (
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={ANIMATION_VARIANTS.slideUp}
            className="mb-8"
          >
            {/* Service Dots Indicator */}
            <div className="flex justify-center gap-2 mb-6">
              {services.map((service) => (
                <button
                  key={service.id}
                  onClick={() => handleTabChange(service.id)}
                  className={`transition-all duration-300 ${
                    activeTab === service.id
                      ? 'w-8 h-2 bg-primary-500 rounded-full'
                      : 'w-2 h-2 bg-gray-600 rounded-full hover:bg-gray-500'
                  }`}
                  aria-label={`Switch to ${service.label}`}
                />
              ))}
            </div>
            
            {/* Navigation Controls with Service Name */}
            <div className="flex items-center justify-center gap-4 max-w-md mx-auto">
              {/* Previous Button */}
              <motion.button
                onClick={navigateToPrevious}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="p-3 bg-gradient-to-r from-primary-500/20 to-accent-500/20 backdrop-blur-sm border border-primary-500/30 rounded-full text-white hover:bg-primary-500/30 transition-all duration-200"
                aria-label="Previous service"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </motion.button>
              
              {/* Current Service Name */}
              <div className="flex-1 text-center">
                <motion.div
                  key={activeService.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  className="px-6 py-3"
                >
                  <div className="text-sm text-primary-400 font-medium mb-1">Service {currentServiceIndex + 1} of {services.length}</div>
                  <div className="text-base text-white font-semibold">{activeService.label}</div>
                </motion.div>
              </div>
              
              {/* Next Button */}
              <motion.button
                onClick={navigateToNext}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="p-3 bg-gradient-to-r from-primary-500/20 to-accent-500/20 backdrop-blur-sm border border-primary-500/30 rounded-full text-white hover:bg-primary-500/30 transition-all duration-200"
                aria-label="Next service"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* Tab Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ 
            duration: 0.5, 
            ease: [0.25, 0.25, 0, 1],
            opacity: { duration: 0.3 },
            y: { duration: 0.5 }
          }}
          className="relative"
          style={{
            willChange: isTransitioning ? 'opacity, transform' : 'auto'
          }}
        >
          {/* Modern Card Layout - Responsive */}
          <div className={`grid ${isMobile ? 'grid-cols-1 gap-6' : 'lg:grid-cols-3 gap-8'} items-stretch`}>
            
            {/* Main Service Card - Full width on mobile, 2 columns on desktop */}
            <motion.div 
              className={`${isMobile ? 'order-2' : 'lg:col-span-2'}`}
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, ease: [0.25, 0.25, 0, 1] }}
            >
              <GlassCard className={`rounded-3xl ${isMobile ? 'p-6' : 'p-8'} relative overflow-hidden h-full flex flex-col group/card hover:shadow-2xl hover:shadow-primary-500/10 transition-all duration-500`}>
                {/* Enhanced Background Pattern */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary-500/10 to-transparent rounded-full blur-3xl group-hover/card:from-primary-400/15 transition-all duration-700"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-accent-500/10 to-transparent rounded-full blur-2xl group-hover/card:from-accent-400/15 transition-all duration-700"></div>
                
                {/* Subtle floating particles effect */}
                <div className="absolute inset-0 opacity-0 group-hover/card:opacity-100 transition-opacity duration-1000">
                  <div className="absolute top-1/4 left-1/4 w-1 h-1 bg-primary-400/60 rounded-full animate-float-slow"></div>
                  <div className="absolute top-3/4 right-1/3 w-1 h-1 bg-accent-400/60 rounded-full animate-float-delayed"></div>
                  <div className="absolute bottom-1/3 left-2/3 w-1 h-1 bg-primary-300/60 rounded-full animate-float"></div>
                </div>
                
                <div className="relative z-40 flex flex-col h-full">
                  {/* Service Header */}
                  <div className="mb-6">
                    <div className="inline-flex items-center px-4 py-2 bg-primary-500/20 rounded-full mb-4">
                      <div className="w-2 h-2 bg-primary-400 rounded-full mr-2 animate-pulse"></div>
                      <span className="text-primary-400 text-sm font-medium">Featured Service</span>
                    </div>
                    <h3 className={`${isMobile ? 'text-2xl' : 'text-3xl'} font-bold text-primary-gradient mb-4 leading-tight`}>
                      {activeService.title}
                    </h3>
                  </div>

                  {/* Service Description */}
                  <p className={`text-gray-200 mb-8 leading-relaxed ${isMobile ? 'text-base' : 'text-lg'}`}>
                    {activeService.description}
                  </p>

                  {/* Feature List - Enhanced with Modern Icons */}
                  <div className="mb-8 flex-grow">
                    <FeatureList features={activeService.features} variant="grid" />
                  </div>

                  {/* Enhanced CTA Buttons - Always at bottom */}
                  <motion.div 
                    className="flex flex-col sm:flex-row gap-4 mt-auto"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.6 }}
                  >
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                      className="flex-1"
                    >
                      <Link
                        to={getServicePath(activeService.id)}
                        className="btn-futuristic w-full text-center flex items-center justify-center gap-2 group/btn"
                        onClick={handlePageNavigation}
                      >
                        <span>Get Started</span>
                        <svg className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </Link>
                    </motion.div>
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                      className="flex-1"
                    >
                      <Link
                        to="/services"
                        className="btn-outline w-full text-center flex items-center justify-center gap-2 group/btn"
                        onClick={handlePageNavigation}
                      >
                        <span>View All Services</span>
                        <svg className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    </motion.div>
                  </motion.div>
                </div>
              </GlassCard>
            </motion.div>

            {/* Image Card - Full width on mobile, 1 column on desktop */}
            <motion.div 
              className={`${isMobile ? 'order-1' : 'lg:col-span-1'}`}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, ease: [0.25, 0.25, 0, 1], delay: 0.2 }}
            >
              <div className={`flex flex-col h-full ${isMobile ? 'space-y-4' : 'space-y-6'}`}>
                {/* Main Image - This will grow to match the other column */}
                <motion.div 
                  className="relative group flex-grow"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                >
                  <div className="rounded-2xl overflow-hidden shadow-2xl bg-gray-900 relative transition-all duration-500 group-hover:shadow-3xl group-hover:shadow-primary-500/20 h-full">
                    <div className={`${isMobile ? 'aspect-video' : 'aspect-square'} h-full`}>
                      <img
                        src={activeService.image}
                        alt={activeService.title}
                        className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105"
                        loading="lazy"
                        decoding="async"
                        style={{
                          willChange: 'transform',
                          transform: 'translateZ(0)',
                        }}
                      />
                      {/* Image Overlay with smooth transition */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30 opacity-0 group-hover:opacity-100 transition-all duration-500">
                        <motion.div 
                          className="absolute bottom-4 left-4 right-4"
                          initial={{ y: 20, opacity: 0 }}
                          whileHover={{ y: 0, opacity: 1 }}
                          transition={{ duration: 0.3 }}
                        >
                          <div className="text-white text-sm font-medium">
                            {activeService.title}
                          </div>
                        </motion.div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Enhanced Floating Badge */}
                  {!isMobile && (
                    <motion.div 
                      className="absolute -top-0 -right-0 bg-gradient-to-r from-primary-500 to-primary-600 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                    >
                      Premium
                    </motion.div>
                  )}
                </motion.div>

                {/* Bottom Cards - Hidden on mobile, shown on desktop */}
                {!isMobile && (
                  <motion.div 
                    className="space-y-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  {/* Stats Cards with Enhanced Animations */}
                  <div className="grid grid-cols-2 gap-3">
                    <motion.div
                      whileHover={{ 
                        scale: 1.05, 
                        rotate: 1,
                        transition: { duration: 0.2 }
                      }}
                    >
                      <GlassCard className="p-4 text-center rounded-xl group/stat hover:border-primary-400/40 hover:shadow-lg hover:shadow-primary-500/20 transition-all duration-300 cursor-pointer">
                        <motion.div 
                          className="text-2xl font-bold bg-gradient-to-r from-primary-400 to-amber-400 bg-clip-text text-transparent mb-1"
                          whileHover={{ scale: 1.1 }}
                          transition={{ duration: 0.2 }}
                        >
                          24/7
                        </motion.div>
                        <div className="text-xs text-gray-300 group-hover/stat:text-gray-200 transition-colors duration-300">Support</div>
                      </GlassCard>
                    </motion.div>
                    <motion.div
                      whileHover={{ 
                        scale: 1.05, 
                        rotate: -1,
                        transition: { duration: 0.2 }
                      }}
                    >
                      <GlassCard className="p-4 text-center rounded-xl group/stat hover:border-primary-400/40 hover:shadow-lg hover:shadow-primary-500/20 transition-all duration-300 cursor-pointer">
                        <motion.div 
                          className="text-2xl font-bold bg-gradient-to-r from-primary-400 to-amber-400 bg-clip-text text-transparent mb-1"
                          whileHover={{ scale: 1.1 }}
                          transition={{ duration: 0.2 }}
                        >
                          99.9%
                        </motion.div>
                        <div className="text-xs text-gray-300 group-hover/stat:text-gray-200 transition-colors duration-300">Uptime</div>
                      </GlassCard>
                    </motion.div>
                  </div>

                  {/* Original "Ready to Deploy" Card for Desktop */}
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.6 }}
                  >
                    <GlassCard className="p-4 rounded-xl hover:border-primary-400/40 hover:shadow-lg hover:shadow-primary-500/20 transition-all duration-300 group/info cursor-pointer">
                      <div className="flex items-center gap-3 mb-3">
                        <motion.div 
                          className="relative"
                          whileHover={{ rotate: 360 }}
                          transition={{ duration: 0.6, ease: "easeInOut" }}
                        >
                          <div className="w-8 h-8 bg-gradient-to-r from-primary-400 to-primary-600 rounded-lg flex items-center justify-center shadow-lg shadow-primary-500/30 group-hover/info:shadow-primary-400/50 transition-all duration-300">
                            <div className="w-3 h-3 bg-white rounded-full"></div>
                          </div>
                        </motion.div>
                        <div>
                          <div className="text-white font-medium text-sm">Ready to Deploy</div>
                          <div className="text-gray-400 text-xs">Instant provisioning available</div>
                        </div>
                      </div>
                      <div className="h-1 bg-gray-700 rounded-full overflow-hidden">
                        <motion.div 
                          className="h-full bg-gradient-to-r from-primary-400 to-primary-600 rounded-full"
                          initial={{ width: '0%' }}
                          animate={{ width: '85%' }}
                          transition={{ duration: 1.5, delay: 0.5, ease: "easeOut" }}
                        />
                      </div>
                    </GlassCard>
                  </motion.div>
                </motion.div>
                )}
              </div>
            </motion.div>
            
            {/* Stats Cards - Shown below service details on mobile only */}
            {isMobile && (
              <motion.div 
                className="order-3 w-full"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                {/* Stats Cards with Enhanced Animations */}
                <div className="grid grid-cols-3 gap-3">
                  <motion.div
                    whileHover={{ 
                      scale: 1.05, 
                      rotate: 1,
                      transition: { duration: 0.2 }
                    }}
                  >
                    <GlassCard className="p-3 text-center rounded-xl group/stat hover:border-primary-400/40 hover:shadow-lg hover:shadow-primary-500/20 transition-all duration-300 cursor-pointer">
                      <motion.div 
                        className="text-xl font-bold bg-gradient-to-r from-primary-400 to-amber-400 bg-clip-text text-transparent mb-1"
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.2 }}
                      >
                        24/7
                      </motion.div>
                      <div className="text-xs text-gray-300 group-hover/stat:text-gray-200 transition-colors duration-300">Support</div>
                    </GlassCard>
                  </motion.div>
                  <motion.div
                    whileHover={{ 
                      scale: 1.05, 
                      transition: { duration: 0.2 }
                    }}
                  >
                    <GlassCard className="p-3 text-center rounded-xl group/stat hover:border-primary-400/40 hover:shadow-lg hover:shadow-primary-500/20 transition-all duration-300 cursor-pointer">
                      <motion.div 
                        className="text-xl font-bold bg-gradient-to-r from-primary-400 to-amber-400 bg-clip-text text-transparent mb-1"
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.2 }}
                      >
                        99.9%
                      </motion.div>
                      <div className="text-xs text-gray-300 group-hover/stat:text-gray-200 transition-colors duration-300">Uptime</div>
                    </GlassCard>
                  </motion.div>
                  <motion.div
                    whileHover={{ 
                      scale: 1.05, 
                      rotate: 1,
                      transition: { duration: 0.2 }
                    }}
                  >
                    <GlassCard className="p-3 text-center rounded-xl group/stat hover:border-emerald-400/40 hover:shadow-lg hover:shadow-emerald-500/20 transition-all duration-300 cursor-pointer">
                      <motion.div 
                        className="flex items-center justify-center gap-1 mb-1"
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                        <div className="text-xl font-bold bg-gradient-to-r from-emerald-400 to-emerald-500 bg-clip-text text-transparent">
                          Ready
                        </div>
                      </motion.div>
                      <div className="text-xs text-gray-300 group-hover/stat:text-emerald-200 transition-colors duration-300">to Deploy</div>
                    </GlassCard>
                  </motion.div>
                </div>
              </motion.div>
            )}

          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default React.memo(ServicesSection);
